export type AmbiguousOperationDomain = 'family' | 'file' | 'goal' | 'financial' | 'client' | 'scenario' | 'pension' | 'financing' | 'comparison' | 'follow-up';
export type PendingOperationState = { domains: AmbiguousOperationDomain[]; healthy: boolean };
export type PendingOperationEvidence = {
  kind: 'file.upload' | 'followup.create';
  key: string;
  requestHash: string;
};
export type PendingOperationEvidenceState = {
  byDomain: Partial<Record<AmbiguousOperationDomain, PendingOperationEvidence>>;
  healthy: boolean;
};

const MAX_PENDING_DOMAINS = 8;
const EVIDENCE_SUFFIX = ':evidence';
const EVIDENCE_KEY = /^[\x21-\x7e]{8,200}$/;
const REQUEST_HASH = /^[a-f0-9]{64}$/;

function storageKey(actorId: string, clientId: string): string {
  return `fintracks:operations:${actorId}:${clientId}`;
}

function evidenceStorageKey(actorId: string, clientId: string): string {
  return `${storageKey(actorId, clientId)}${EVIDENCE_SUFFIX}`;
}

function validDomain(value: unknown): value is AmbiguousOperationDomain {
  return value === 'family' || value === 'file' || value === 'goal' || value === 'financial' || value === 'client' || value === 'scenario' || value === 'pension' || value === 'financing' || value === 'comparison' || value === 'follow-up';
}

function validEvidence(value: unknown): value is PendingOperationEvidence {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return (record.kind === 'file.upload' || record.kind === 'followup.create')
    && typeof record.key === 'string' && EVIDENCE_KEY.test(record.key)
    && typeof record.requestHash === 'string' && REQUEST_HASH.test(record.requestHash);
}

function validEvidenceForDomain(domain: string, value: unknown): value is PendingOperationEvidence {
  if (!validEvidence(value)) return false;
  return (domain === 'file' && value.kind === 'file.upload') || (domain === 'follow-up' && value.kind === 'followup.create');
}

export function loadPendingOperations(actorId: string, clientId: string): PendingOperationState {
  if (!actorId || !clientId || typeof localStorage === 'undefined') return { domains: [], healthy: false };
  try {
    const raw = JSON.parse(localStorage.getItem(storageKey(actorId, clientId)) ?? '[]') as unknown;
    if (!Array.isArray(raw) || raw.length > MAX_PENDING_DOMAINS || raw.some((item) => !validDomain(item))) return { domains: [], healthy: false };
    return { domains: [...new Set(raw)], healthy: true };
  } catch {
    return { domains: [], healthy: false };
  }
}

export function loadPendingOperationEvidence(actorId: string, clientId: string): PendingOperationEvidenceState {
  if (!actorId || !clientId || typeof localStorage === 'undefined') return { byDomain: {}, healthy: false };
  try {
    const raw = JSON.parse(localStorage.getItem(evidenceStorageKey(actorId, clientId)) ?? '{}') as unknown;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { byDomain: {}, healthy: false };
    const byDomain: Partial<Record<AmbiguousOperationDomain, PendingOperationEvidence>> = {};
    for (const [domain, evidence] of Object.entries(raw)) {
      if (!validDomain(domain) || !validEvidenceForDomain(domain, evidence)) return { byDomain: {}, healthy: false };
      byDomain[domain] = evidence;
    }
    return { byDomain, healthy: true };
  } catch {
    return { byDomain: {}, healthy: false };
  }
}

async function updatePendingOperations(
  actorId: string,
  clientId: string,
  operation: (current: PendingOperationState) => PendingOperationState,
  evidenceChange?: { domain: AmbiguousOperationDomain; evidence: PendingOperationEvidence | null },
): Promise<PendingOperationState> {
  if (!actorId || !clientId || typeof localStorage === 'undefined') return { domains: [], healthy: false };
  try {
    return await withOperationSafetyLock(actorId, clientId, async () => {
      const current = loadPendingOperations(actorId, clientId);
      if (!current.healthy) return current;
      const next = operation(current);
      if (!next.healthy) return next;
      if (evidenceChange) {
        const evidenceState = loadPendingOperationEvidence(actorId, clientId);
        if (!evidenceState.healthy) return { domains: current.domains, healthy: false };
        const byDomain = { ...evidenceState.byDomain };
        if (evidenceChange.evidence) byDomain[evidenceChange.domain] = evidenceChange.evidence;
        else delete byDomain[evidenceChange.domain];
        if (Object.keys(byDomain).length === 0) localStorage.removeItem(evidenceStorageKey(actorId, clientId));
        else localStorage.setItem(evidenceStorageKey(actorId, clientId), JSON.stringify(byDomain));
      }
      localStorage.setItem(storageKey(actorId, clientId), JSON.stringify(next.domains));
      return next;
    });
  } catch {
    return { domains: loadPendingOperations(actorId, clientId).domains, healthy: false };
  }
}

export async function rememberPendingOperation(actorId: string, clientId: string, domain: AmbiguousOperationDomain, evidence?: PendingOperationEvidence): Promise<PendingOperationState> {
  if (!validDomain(domain) || (evidence !== undefined && !validEvidenceForDomain(domain, evidence))) {
    return { domains: loadPendingOperations(actorId, clientId).domains, healthy: false };
  }
  return updatePendingOperations(actorId, clientId, (current) => {
    const domains = [...new Set([...current.domains, domain])];
    return domains.length > MAX_PENDING_DOMAINS ? { domains: current.domains, healthy: false } : { domains, healthy: true };
  }, { domain, evidence: evidence ?? null });
}

export async function clearPendingOperations(actorId: string, clientId: string): Promise<PendingOperationState> {
  if (!actorId || !clientId || typeof localStorage === 'undefined') return { domains: [], healthy: false };
  try {
    return await withOperationSafetyLock(actorId, clientId, async () => {
      localStorage.removeItem(storageKey(actorId, clientId));
      localStorage.removeItem(evidenceStorageKey(actorId, clientId));
      return { domains: [], healthy: true };
    });
  } catch {
    return { domains: loadPendingOperations(actorId, clientId).domains, healthy: false };
  }
}

export async function clearPendingOperationDomain(actorId: string, clientId: string, domain: AmbiguousOperationDomain): Promise<PendingOperationState> {
  return updatePendingOperations(actorId, clientId, (current) => ({ domains: current.domains.filter((item) => item !== domain), healthy: true }), { domain, evidence: null });
}

export function isAmbiguousMutationError(error: unknown): boolean {
  const code = error && typeof error === 'object' && 'code' in error ? String((error as { code?: unknown }).code ?? '') : '';
  return code === 'OUTCOME_UNKNOWN' || code === 'DIRECTUS_OUTCOME_UNKNOWN' || code.endsWith('_OUTCOME_UNKNOWN') || code.endsWith('_OUTCOME_UNKNOWN_RECONCILIATION_REQUIRED') || code.endsWith('_PARENT_CLOSE_FAILED');
}

function operationSafetyError(message: string): Error & { code: 'OPERATION_STORAGE_UNSAFE' } {
  const error = new Error(message) as Error & { code: 'OPERATION_STORAGE_UNSAFE' };
  error.code = 'OPERATION_STORAGE_UNSAFE';
  return error;
}

export function isOperationSafetyFailure(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const record = error as { code?: unknown; message?: unknown };
  return record.code === 'OPERATION_STORAGE_UNSAFE'
    || (typeof record.message === 'string' && (/Web Locks/.test(record.message) || /armazenamento local das operações/.test(record.message)));
}

/**
 * Reserva o domínio antes da chamada remota e só o libera sob o mesmo lock.
 * Em outcome ambíguo a reserva permanece, impedindo outra aba de gravar antes
 * que a reconciliação confirme o resultado.
 */
export async function withOperationSafetyMutation<T>(actorId: string, clientId: string, domain: AmbiguousOperationDomain, operation: () => Promise<T>): Promise<T> {
  if (!validDomain(domain)) throw operationSafetyError('Domínio de operação inválido.');
  return withOperationSafetyLock(actorId, clientId, async () => {
    const current = loadPendingOperations(actorId, clientId);
    if (!current.healthy) throw operationSafetyError('O armazenamento local das operações está indisponível; não repita a operação.');
    ensureNoPendingOperation(current);
    const reserved = [...current.domains, domain];
    try {
      localStorage.setItem(storageKey(actorId, clientId), JSON.stringify(reserved));
    } catch {
      throw operationSafetyError('Não foi possível reservar a operação no armazenamento local; não repita.');
    }
    const release = () => {
      try {
        const latest = loadPendingOperations(actorId, clientId);
        if (!latest.healthy) throw operationSafetyError('O armazenamento local das operações ficou indisponível; não repita.');
        localStorage.setItem(storageKey(actorId, clientId), JSON.stringify(latest.domains.filter((item) => item !== domain)));
      } catch (error) {
        if (isOperationSafetyFailure(error)) throw error;
        throw operationSafetyError('Não foi possível atualizar o journal local da operação; não repita.');
      }
    };
    try {
      const result = await operation();
      release();
      return result;
    } catch (error) {
      if (!isAmbiguousMutationError(error)) release();
      throw error;
    }
  });
}

export function ensureNoPendingOperation(state: PendingOperationState): void {
  if (!state.healthy) throw new Error('O armazenamento local das operações está indisponível; não repita a operação até recuperar a sessão segura.');
  if (state.domains.length > 0) throw new Error('Há uma operação sem resultado confirmado; reconcilie antes de tentar novamente.');
}

export async function withOperationSafetyLock<T>(actorId: string, clientId: string, operation: () => Promise<T>): Promise<T> {
  if (typeof window === 'undefined') return operation();
  const nodeEnv = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env?.NODE_ENV;
  if (nodeEnv === 'test') return operation();
  type LockManagerLike = { request<R>(name: string, options: { mode: 'exclusive' }, callback: () => Promise<R>): Promise<R> };
  const locks = typeof navigator !== 'undefined' && 'locks' in navigator
    ? (navigator as Navigator & { locks?: LockManagerLike }).locks
    : undefined;
  if (window.isSecureContext !== true || !locks?.request) throw new Error('Este navegador não oferece Web Locks em contexto seguro para coordenar a operação entre abas.');
  let entered = false;
  try {
    return await locks.request(`fintracks:operation:${actorId}:${clientId}`, { mode: 'exclusive' }, async () => {
      entered = true;
      return operation();
    });
  } catch (error) {
    if (!entered) throw operationSafetyError('Não foi possível adquirir o Web Lock seguro; não repita a operação.');
    throw error;
  }
}
